import { LANDING_PAGES, LANDING_PREFIX, landingImage } from './landingPages';
import { SERVICE_LANDINGS, serviceLandingImage } from './services/landings';
import { SERVICE_LINE_LIST } from './services/lines';
export interface PageSEOMeta {
  title: string;
  description: string;
  keywords: string;
  /** schema.org @type for primary entity */
  schemaType: 'WebSite' | 'AboutPage' | 'ContactPage' | 'ProfessionalService' | 'WebPage' | 'Blog' | 'CollectionPage';
  /** Extra schema fields merged into the primary entity */
  schemaExtras?: Record<string, unknown>;
  /** Optional page-specific social image (site-relative or absolute), 1200x630. */
  ogImage?: string;
}

/**
 * The single source of truth for every static page's <title> and meta
 * description. Both the runtime <head> pass (src/hooks/useSEO.ts) and the
 * build-time prerenderer (scripts/prerender.mjs) resolve from this map, so the
 * two can never drift.
 *
 * Authoring rules (enforced by the SEO audit at build time):
 *   • title        50–60 characters, primary keyword first, brand last ("| Cendien")
 *   • description  140–158 characters, natural, unique across the site
 *   • every title and description must be unique
 */
/** Headerless SEO landing pages (/managed-it/{slug}), defined in landingPages.ts. */
const LANDING_SEO: Record<string, PageSEOMeta> = Object.fromEntries(
  LANDING_PAGES.map((p) => [
    `${LANDING_PREFIX}${p.slug}`,
    {
      title: p.title,
      description: p.description,
      keywords: p.keywords,
      schemaType: 'ProfessionalService' as const,
      schemaExtras: {
        serviceType: 'Managed IT Services',
        areaServed: p.areaServed,
        image: `https://cendien.com${landingImage(p.slug).og}`,
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'USD',
          lowPrice: 28,
          highPrice: 120,
          offerCount: 6,
          url: `https://cendien.com${LANDING_PREFIX}${p.slug}#packages`,
        },
      },
      ogImage: landingImage(p.slug).og,
    },
  ]),
);

/** Pricing calculators for the Infor, Workday, staffing, AI and development lines. */
const SERVICE_PRICING_META: Record<string, { title: string; description: string; keywords: string }> = {
  infor: {
    title: 'Infor Managed Services Pricing and Cost Calculator | Cendien',
    description: 'Estimate Infor managed services cost by weekly hours: $175, $170 or $165 an hour by tier, billed every 4 weeks. CloudSuite and Lawson support packages.',
    keywords: 'Infor managed services pricing, Infor support cost, Lawson support cost, Infor consultant rates, Infor AMS pricing',
  },
  workday: {
    title: 'Workday Managed Services Pricing Calculator | Cendien',
    description: 'Estimate Workday managed services cost by weekly hours: $175, $170 or $165 an hour by tier, billed every 4 weeks. HCM, Payroll and Financials packages.',
    keywords: 'Workday managed services pricing, Workday AMS cost, Workday support cost, Workday consultant rates, Workday post production support cost',
  },
  staffing: {
    title: 'IT Staffing Rates and Cost Calculator (2026) | Cendien',
    description: 'IT staffing rates for 11 roles, from $55 an hour for help desk to $235 for AI engineers. Estimate contract, contract-to-hire and direct-hire costs.',
    keywords: 'IT staffing rates, IT contractor rates, IT staffing cost calculator, contract developer rates, direct hire fee, IT staffing agency rates',
  },
  ai: {
    title: 'AI Consulting Cost Calculator and Pricing (2026) | Cendien',
    description: 'Estimate the cost of AI assistants, agents and Copilot rollouts. Fixed-price AI assessments from $15,000 and pilots from $18,000, with governance built in.',
    keywords: 'AI consulting cost, AI implementation cost, AI pilot cost, Copilot implementation cost, AI agent development cost, AI readiness assessment price',
  },
  dev: {
    title: 'Custom Software Development Cost Calculator (2026) | Cendien',
    description: 'Estimate custom software, web app and integration costs at a blended $125 to $150 an hour. Fixed-price discovery sprint and MVP packages from $45,000.',
    keywords: 'custom software development cost, web app development cost, software development pricing, app development cost calculator, development hourly rate',
  },
};
const SERVICE_SEO: Record<string, PageSEOMeta> = {
  ...Object.fromEntries(SERVICE_LINE_LIST.map((l) => [l.pricingPath, {
    ...SERVICE_PRICING_META[l.id],
    schemaType: 'WebPage' as const,
    schemaExtras: { about: { '@type': 'Service', serviceType: l.name, provider: { '@type': 'Organization', name: 'Cendien' } } },
  }])),
  ...Object.fromEntries(SERVICE_LANDINGS.map((p) => {
    const line = SERVICE_LINE_LIST.find((l) => l.id === p.line)!;
    const prices = line.plans.filter((x) => x.schemaPrice).flatMap((x) => [x.schemaPrice!.min, x.schemaPrice!.max]);
    return [`${line.landingPrefix}${p.slug}`, {
      title: p.title,
      description: p.description,
      keywords: p.keywords,
      schemaType: 'ProfessionalService' as const,
      schemaExtras: {
        serviceType: line.name,
        areaServed: p.areaServed,
        image: `https://cendien.com${serviceLandingImage(p).og}`,
        ...(prices.length ? { offers: { '@type': 'AggregateOffer', priceCurrency: 'USD', lowPrice: Math.min(...prices), highPrice: Math.max(...prices), offerCount: line.plans.length, url: `https://cendien.com${line.landingPrefix}${p.slug}#packages` } } : {}),
      },
      ogImage: serviceLandingImage(p).og,
    }];
  })),
};

export const SEO_PAGES: Record<string, PageSEOMeta> = {
  ...LANDING_SEO,
  ...SERVICE_SEO,
  '/': {
    title: 'IT Consulting & Staffing Firm in Dallas, TX | Cendien',
    description:
      'Cendien is an IT consulting, staffing and managed services firm in Carrollton, TX, serving Dallas–Fort Worth with ERP, cloud and AI since 1999.',
    keywords: 'IT consulting Carrollton TX, IT staffing Dallas, ERP services Texas, cloud managed services, AI consulting, digital transformation',
    schemaType: 'WebSite',
  },
  '/about': {
    title: 'About Our IT Consulting & Staffing Company | Cendien',
    description:
      "Learn about Cendien's story, leadership, mission and values — and how our Dallas–Fort Worth IT consulting and staffing team has helped clients since 1999.",
    keywords: 'about Cendien, IT consulting company Texas, IT staffing firm Carrollton, Cendien history, IT consulting DFW',
    schemaType: 'AboutPage',
  },
  '/about/our-story': {
    title: 'Our Story: IT Consulting Firm Since 1999 | Cendien',
    description:
      'Discover how Cendien grew from a Dallas–Fort Worth IT staffing firm into a full-service IT consulting, ERP, cloud and AI company serving clients nationwide.',
    keywords: "Cendien history, IT consulting company story, Carrollton Texas IT firm, DFW IT services history",
    schemaType: 'AboutPage',
  },
  '/about/mission-values': {
    title: 'Mission & Values | IT Consulting in Dallas | Cendien',
    description:
      "Cendien's mission is to empower businesses with transformative technology. Explore our core values of integrity, innovation, excellence and true partnership.",
    keywords: 'Cendien mission values, IT consulting company values, technology transformation, Carrollton TX IT firm',
    schemaType: 'AboutPage',
  },
  '/about/why-cendien': {
    title: 'Why Choose Our IT Consulting Firm in Texas | Cendien',
    description:
      'See why enterprise and public-sector clients trust Cendien for ERP, cloud, AI, staffing and managed IT services across Dallas–Fort Worth since 1999.',
    keywords: 'why Cendien, IT consulting differentiators Texas, enterprise IT partner, ERP cloud AI staffing',
    schemaType: 'AboutPage',
  },
  '/about/certifications': {
    title: 'IT Certifications, MBE & Vendor Credentials | Cendien',
    description:
      'Cendien is a Texas DIR Vendor, Certified MBE (NCTRCA #HMMB47549N0725) and Microsoft Certified Partner, with Infor, Oracle and NCTCOG DBITS credentials.',
    keywords: 'Cendien certifications, Microsoft Certified Partner, Texas DIR vendor, Certified MBE NCTRCA, Infor partner, Oracle partner, NCTCOG DBITS, NAICS 541512',
    schemaType: 'AboutPage',
  },
  '/about/dallas-coverage': {
    title: 'Managed IT Services in Carrollton, Dallas & DFW | Cendien',
    description:
      'Cendien delivers on-site IT consulting, staffing and managed services across Dallas, Collin, Denton and Tarrant counties, with remote support nationwide.',
    keywords: 'Dallas IT coverage, DFW IT services, Carrollton IT company, on-site IT support Dallas, Collin Denton Tarrant county IT',
    schemaType: 'AboutPage',
  },
  '/contact': {
    title: 'Contact Our IT Consulting & Staffing Firm | Cendien',
    description:
      'Reach Cendien at (214) 245-4580 or visit our Carrollton, TX headquarters at 1846 E. Rosemeade Pkwy, Suite 200. Get in touch about IT services.',
    keywords: 'contact Cendien, IT consulting Carrollton TX phone, ERP staffing contact, Cendien address',
    schemaType: 'ContactPage',
  },
  '/services': {
    title: 'IT Consulting Firm in Dallas–Fort Worth, TX | Cendien',
    description:
      'Cendien delivers end-to-end IT services across Texas — ERP implementation, cloud migration, AI automation, IT staffing, managed services and healthcare IT.',
    keywords: 'IT services Texas, ERP implementation, cloud migration, AI automation, IT staffing, managed services Carrollton',
    schemaType: 'CollectionPage',
  },
  '/services/staffing': {
    title: 'IT Staffing & ERP Consultants in Dallas, TX | Cendien',
    description:
      'Cendien provides contract IT staffing DFW and IT staff augmentation Dallas, placing ERP consultants, Infor consultants for hire and Workday consultants.',
    keywords: 'IT staff augmentation Dallas, contract IT staffing DFW, ERP consultants, Infor consultants for hire, Workday consultants',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'IT Staffing' },
  },
  '/services/staffing/contract': {
    title: 'Contract IT Staffing in Dallas–Fort Worth | Cendien',
    description:
      'Access on-demand contract IT professionals across Dallas–Fort Worth. Cendien places ERP consultants, cloud engineers and IT specialists on flexible terms.',
    keywords: 'contract IT staffing Dallas, on-demand IT talent, ERP contractor DFW, cloud engineer contract Texas',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Contract IT Staffing' },
  },
  '/services/staffing/direct-hire': {
    title: 'Direct Hire IT Staffing Services in Dallas | Cendien',
    description:
      "Cendien's direct hire IT staffing places permanent technology professionals across Dallas, Carrollton and Texas — backed by a 90-day replacement guarantee.",
    keywords: 'direct hire IT Dallas, permanent IT placement Texas, IT recruitment DFW, technology hiring Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Direct Hire IT Staffing' },
  },
  '/services/staffing/managed-teams': {
    title: 'IT Staff Augmentation & Dedicated Managed Teams | Cendien',
    description:
      'Cendien builds and manages dedicated IT teams for your business, with flexible engagement models, full governance and scalable structures across Texas.',
    keywords: 'managed IT teams Texas, dedicated IT team, team as a service DFW, outsourced IT team Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Managed IT Teams' },
  },
  '/services/staffing/executive-search': {
    title: 'IT Executive Search: CIO & CTO Placement | Cendien',
    description:
      'Cendien specializes in executive IT searches across Texas, placing CIOs, CTOs, IT Directors and VP-level technology leaders with enterprise organizations.',
    keywords: 'IT executive search Dallas, CTO placement Texas, CIO recruitment DFW, technology leadership search',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Executive IT Search' },
  },
  '/services/erp': {
    title: 'ERP Services in Dallas: Implementation & Support | Cendien',
    description:
      'Cendien provides end-to-end ERP services — implementation, managed services, upgrades, integration, training and consulting — for Microsoft, Infor and Oracle.',
    keywords: 'ERP services Dallas TX, ERP implementation Texas, ERP consulting Carrollton, Infor ERP, Microsoft Dynamics ERP',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'ERP Services' },
  },
  '/services/erp/implementation': {
    title: 'ERP Implementation Services in Dallas, TX | Cendien',
    description:
      'Cendien delivers proven ERP implementations for Microsoft Dynamics, Infor and Oracle platforms — on time, on budget and across Texas and nationwide.',
    keywords: 'ERP implementation Dallas, Microsoft Dynamics implementation TX, Infor ERP implementation, ERP deployment Texas',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'ERP Implementation' },
  },
  '/services/erp/managed-services': {
    title: 'ERP Application Managed Services in Dallas | Cendien',
    description:
      'Cendien is an ERP managed services provider delivering ERP application managed services — support, monitoring, upgrades and cloud migration.',
    keywords: 'ERP application managed services, ERP managed services provider, ERP AMS Dallas, Oracle ERP managed support, PeopleSoft managed services',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'ERP Managed Services' },
  },
  '/services/erp/infor-support': {
    title: 'Infor Lawson Support & CloudSuite Managed Services | Cendien',
    description:
      'Cendien delivers managed Infor Lawson and CloudSuite support — functional help, tax and regulatory patches, year-end and Birst/IPA/ISD reporting.',
    keywords: 'Infor Lawson support, CloudSuite managed services, Infor AMS, Lawson payroll support, Birst IPA ISD reporting, CSI SyteLine support',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Infor Lawson & CloudSuite Support' },
  },
  '/services/erp/infor-lawson': {
    title: 'Infor Lawson Support, Upgrades & Payroll | Cendien',
    description:
      'Cendien provides Infor Lawson support and managed services — HR, payroll and financials, tax updates, IPA/ProcessFlow, reporting, security and upgrades.',
    keywords: 'Infor Lawson support, Lawson payroll support, Lawson tax updates, Lawson upgrades, IPA ProcessFlow, Lawson managed services, Infor partner',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Infor Lawson Support & Managed Services' },
  },
  '/services/erp/infor-cloudsuite': {
    title: 'Infor CloudSuite Managed Services, FSM & HCM | Cendien',
    description:
      'Cendien delivers Infor CloudSuite managed services — FSM and HCM support, Birst reporting, ION and IPA integrations, ISD and CSI/SyteLine support.',
    keywords: 'Infor CloudSuite managed services, CloudSuite FSM support, CloudSuite HCM support, Birst reporting, ION IPA integrations, ISD support, SyteLine CSI support',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Infor CloudSuite Managed Services' },
  },
  '/services/erp/workday-support': {
    title: 'Workday Support & Managed Services in Dallas | Cendien',
    description:
      'Cendien provides Workday managed services and Workday support services — Workday HCM support, integration support, reporting, security and AMS.',
    keywords: 'Workday managed services, Workday support services, Workday HCM support, Workday integration support, Workday consulting services, Workday consultants',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Workday Support & AMS' },
  },
  '/services/erp/oracle-support': {
    title: 'Oracle ERP Support & Managed Services in Dallas | Cendien',
    description:
      'Cendien delivers Oracle ERP support and managed services — Oracle E-Business Suite and Oracle Cloud ERP/HCM functional and technical support, reporting.',
    keywords: 'Oracle ERP support, Oracle Cloud ERP support, Oracle E-Business Suite support, Oracle Cloud HCM support, Oracle managed services, Oracle upgrades',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Oracle ERP Support & Managed Services' },
  },
  '/services/erp/peoplesoft-support': {
    title: 'PeopleSoft Support & Managed Services in Dallas | Cendien',
    description:
      'Cendien delivers PeopleSoft support and managed services — HCM, Payroll, Financials and Campus Solutions, PeopleTools upgrades, PUM updates and reporting.',
    keywords: 'PeopleSoft support, PeopleSoft managed services, PeopleSoft HCM support, PeopleSoft Payroll support, PeopleTools upgrade support, PUM image updates',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'PeopleSoft Support & Managed Services' },
  },
  '/services/erp/upgrades': {
    title: 'ERP Upgrades & Migrations in Dallas, Texas | Cendien',
    description:
      "Upgrade or migrate your ERP with Cendien's proven methodology — minimize risk, preserve data integrity and unlock new capabilities with expert consultants.",
    keywords: 'ERP upgrade Dallas TX, ERP migration Texas, Microsoft Dynamics upgrade, Infor ERP migration, ERP modernization',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'ERP Upgrades & Migrations' },
  },
  '/services/erp/integration': {
    title: 'ERP Integration Services & APIs | Dallas | Cendien',
    description:
      'Cendien connects your ERP to CRM, e-commerce, payroll and third-party systems using APIs, middleware and custom integration patterns across Texas.',
    keywords: 'ERP integration Dallas TX, ERP API integration Texas, system integration Carrollton, ERP middleware DFW',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'ERP Integration' },
  },
  '/services/erp/training': {
    title: 'ERP Training for End-Users & Admins in Texas | Cendien',
    description:
      'Cendien offers on-site and virtual ERP training for end-users and administrators across Texas, covering Microsoft Dynamics, Infor and custom ERP systems.',
    keywords: 'ERP training Dallas TX, end-user ERP training Texas, Dynamics 365 training, Infor ERP training Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'ERP Training' },
  },
  '/services/erp/consulting': {
    title: 'ERP Consulting & Process Optimization Services | Cendien',
    description:
      'Cendien ERP consultants assess, optimize and transform your business processes — strategic ERP consulting for manufacturing, healthcare, finance and more.',
    keywords: 'ERP consulting Dallas TX, ERP business process optimization, ERP strategy Texas, ERP assessment Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'ERP Consulting' },
  },
  '/services/managed': {
    title: 'Managed IT Services Dallas & DFW | Co-Managed IT | Cendien',
    description:
      'Cendien delivers managed IT services in Dallas and co-managed IT services across DFW — outsourced IT support, help desk, monitoring, patching and vCIO.',
    keywords: 'managed IT services in Dallas, co-managed IT services, outsourced IT support, IT help desk outsourcing DFW, vCIO services',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Managed IT Services' },
  },
  '/services/managed/pricing': {
    title: 'Managed IT Services Cost Calculator & Pricing | Cendien',
    description:
      'Estimate managed IT services cost in seconds. Six Cendien packages run $28 to $120 per user per month, tools included. Get a fixed monthly quote.',
    keywords: 'managed IT services cost calculator, managed IT services pricing, IT support cost calculator, managed IT cost per user, MSP pricing 2026, managed IT pricing Dallas, government managed IT pricing',
    schemaType: 'WebPage',
    schemaExtras: { about: { '@type': 'Service', serviceType: 'Managed IT Services', provider: { '@type': 'Organization', name: 'Cendien' } } },
  },
  '/services/managed/packages': {
    title: 'Managed IT Packages & Pricing Per User (2026) | Cendien',
    description:
      'Compare six managed IT packages from $28 per user per month, from Security Only to Compliance, plus à la carte security, backup and vCISO services.',
    keywords: 'managed IT packages, managed IT services pricing per user, MSP packages, managed IT plans, good better best managed IT, co-managed IT pricing, managed security pricing, managed IT a la carte, vCISO pricing, Microsoft 365 backup pricing',
    schemaType: 'WebPage',
    schemaExtras: { about: { '@type': 'Service', serviceType: 'Managed IT Services', provider: { '@type': 'Organization', name: 'Cendien' } } },
  },
  '/services/managed/infrastructure': {
    title: 'Managed Infrastructure & Network Services | Cendien',
    description:
      'Cendien manages your network, servers, storage and IT infrastructure with proactive monitoring and rapid response for Dallas-area businesses.',
    keywords: 'managed infrastructure Dallas, network management Texas, server management Carrollton, IT infrastructure DFW',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Managed Infrastructure' },
  },
  '/services/managed/security': {
    title: 'Cybersecurity Services in Dallas & DFW, TX | Cendien',
    description:
      'Cendien provides managed cybersecurity and compliance services across Texas — threat detection, vulnerability management, HIPAA, SOC 2 and CMMC support.',
    keywords: 'managed cybersecurity Dallas, IT security compliance Texas, HIPAA compliance Carrollton, SOC 2 managed security DFW',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Managed Cybersecurity' },
  },
  '/services/service-desk': {
    title: 'IT Service Desk & Help Desk in Dallas, TX | Cendien',
    description:
      'Cendien operates a full-service IT help desk — L1, L2 and field support — for businesses across Dallas–Fort Worth, with SLA-backed response times.',
    keywords: 'IT service desk Dallas TX, help desk support Texas, L1 L2 IT support Carrollton, field IT support DFW',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'IT Service Desk' },
  },
  '/services/service-desk/l1-l2-support': {
    title: 'L1 & L2 IT Help Desk Support | Dallas, TX | Cendien',
    description:
      'Cendien delivers Tier 1 and Tier 2 IT help desk support across Dallas–Fort Worth, with guaranteed SLAs, remote and on-site help and mature ITSM tooling.',
    keywords: 'L1 L2 help desk Dallas, IT support tiers Texas, tier 1 tier 2 IT support DFW, help desk SLA Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'L1/L2 Help Desk Support' },
  },
  '/services/service-desk/field-support': {
    title: 'Field IT Support & On-Site Dispatch Services | Cendien',
    description:
      'Cendien dispatches certified field technicians across Dallas–Fort Worth for on-site IT support, break-fix, hardware deployment and network maintenance.',
    keywords: 'field IT support Dallas TX, on-site IT dispatch Texas, break-fix IT DFW, field technician Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Field IT Support' },
  },
  '/services/ai': {
    title: 'AI Automation Consulting Services in Dallas | Cendien',
    description:
      'Cendien delivers enterprise AI services — strategy, automation, machine learning, generative AI and business intelligence — for Texas and national clients.',
    keywords: 'AI services Dallas TX, AI automation Texas, machine learning consulting, generative AI DFW, enterprise AI Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'AI & Automation Services' },
  },
  '/services/ai/agents': {
    title: 'AI Agents for ERP, IT Operations & Public Sector | Cendien',
    description:
      'Cendien delivers governed AI agents for ERP, IT operations and public agencies — automating routine HR and help desk work inside the systems you already run.',
    keywords: 'AI agents, governed AI agents, ERP AI agents, AIOps agents, help desk automation, AI for public sector, Infor HR AI agents, Cendien',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'AI Agents' },
  },
  '/services/ai/strategy': {
    title: 'AI Strategy Consulting & Roadmaps in Dallas | Cendien',
    description:
      'Cendien AI strategists build custom AI roadmaps and maturity assessments for enterprise clients across Texas, starting your AI transformation on solid ground.',
    keywords: 'AI strategy Dallas TX, AI roadmap consulting Texas, AI maturity assessment DFW, enterprise AI planning',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'AI Strategy Consulting' },
  },
  '/services/ai/automation': {
    title: 'AI Automation & Microsoft Copilot Consulting | Cendien',
    description:
      'Cendien provides AI automation consulting Dallas and Microsoft Copilot consulting, plus business process automation services in Dallas–Fort Worth.',
    keywords: 'AI automation consulting Dallas, Microsoft Copilot consulting, business process automation services, Copilot readiness assessment',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Copilot Implementation & Business Process Automation' },
  },
  '/services/ai/machine-learning': {
    title: 'Machine Learning & Predictive AI | Dallas | Cendien',
    description:
      'Cendien builds and deploys custom machine learning models for predictive analytics, anomaly detection and intelligent decisions for Texas businesses.',
    keywords: 'machine learning Dallas TX, ML consulting Texas, predictive analytics DFW, custom AI models Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Machine Learning' },
  },
  '/services/ai/generative-ai': {
    title: 'Generative AI & LLM Solutions in Dallas, TX | Cendien',
    description:
      'Cendien implements enterprise generative AI — custom LLMs, RAG pipelines, chatbots and content automation — for clients across Texas and nationwide.',
    keywords: 'generative AI Dallas TX, LLM enterprise solutions Texas, ChatGPT enterprise DFW, GenAI consulting Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Generative AI' },
  },
  '/services/ai/app-development': {
    title: 'AI Application Development in Dallas, TX | Cendien',
    description:
      'Cendien develops intelligent AI-powered applications — from chatbots to recommendation engines — tailored for enterprise clients across Texas.',
    keywords: 'AI app development Dallas TX, intelligent application development Texas, AI-powered apps DFW, custom AI software',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'AI Application Development' },
  },
  '/services/ai/business-intelligence': {
    title: 'Power BI & Business Intelligence in Dallas, TX | Cendien',
    description:
      'Cendien delivers BI dashboards, data warehousing and advanced analytics for Texas enterprises using Power BI, Tableau and custom data platforms.',
    keywords: 'business intelligence Dallas TX, BI consulting Texas, data analytics DFW, Power BI Tableau Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Business Intelligence' },
  },
  '/services/ai-data': {
    title: 'AI & Data Services: Pipelines & Analytics | Cendien',
    description:
      'Cendien provides AI and data services — data engineering, pipelines, analytics platforms and AI integration — for enterprises across Texas and beyond.',
    keywords: 'AI data services Dallas TX, data engineering Texas, data pipeline DFW, analytics consulting Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'AI & Data Services' },
  },
  '/services/healthcare-it': {
    title: 'Healthcare IT Services & Consulting in Dallas | Cendien',
    description:
      'Cendien delivers healthcare IT consulting — EHR implementation, interoperability, HIPAA compliance and health data management — for Texas health systems.',
    keywords: 'healthcare IT Dallas TX, EHR consulting Texas, healthcare interoperability DFW, HIPAA IT services Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Healthcare IT Services' },
  },
  '/services/healthcare-it/ehr': {
    title: 'EHR Implementation & Consulting in Dallas | Cendien',
    description:
      'Cendien implements and optimizes electronic health record systems including Epic and Cerner for hospitals, clinics and health networks across Texas.',
    keywords: 'EHR implementation Dallas TX, Epic EHR consulting Texas, Cerner implementation DFW, electronic health records Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'EHR Implementation' },
  },
  '/services/healthcare-it/interoperability': {
    title: 'Healthcare Interoperability: HL7 & FHIR in Texas | Cendien',
    description:
      'Cendien enables seamless healthcare data exchange with HL7, FHIR and custom APIs for Texas health systems, care networks and clinical data teams.',
    keywords: 'healthcare interoperability Dallas TX, HL7 FHIR integration Texas, health data exchange DFW, interoperability consulting',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Healthcare Interoperability' },
  },
  '/services/cloud': {
    title: 'Cloud Services: Migration & Managed Cloud | Cendien',
    description:
      'Cendien delivers cloud consulting, migration and managed cloud services on AWS, Azure and Google Cloud for enterprises across Texas and the U.S.',
    keywords: 'cloud services Dallas TX, cloud migration Texas, managed cloud DFW, AWS Azure cloud consulting Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Cloud Services' },
  },
  '/services/cloud/migration': {
    title: 'Cloud Migration Services: AWS, Azure, GCP | Cendien',
    description:
      'Cendien migrates on-premises infrastructure to AWS, Azure and Google Cloud with minimal downtime and maximum data integrity for Texas businesses.',
    keywords: 'cloud migration Dallas TX, AWS migration Texas, Azure migration DFW, GCP migration Carrollton, lift-and-shift cloud',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Cloud Migration' },
  },
  '/services/cloud/managed-cloud': {
    title: 'Managed Cloud Services & 24/7 Operations | Cendien',
    description:
      'Cendien manages your AWS, Azure and GCP environment with continuous monitoring, cost optimization and security operations for Texas organizations.',
    keywords: 'managed cloud Dallas TX, cloud operations Texas, AWS managed services DFW, Azure managed cloud Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Managed Cloud' },
  },
  '/services/app/app-development': {
    title: 'Custom Application Development in Dallas | Cendien',
    description:
      'Cendien builds custom web, mobile and enterprise applications for Texas businesses using React, .NET, Node.js and cloud-native architectures.',
    keywords: 'custom app development Dallas TX, enterprise application development Texas, web app development DFW, software development Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Application Development' },
  },
  '/services/app/field-support': {
    title: 'Application Field Support & Maintenance Services | Cendien',
    description:
      'Cendien provides on-site and remote application field support, break-fix and maintenance for enterprise software across Texas and nationwide.',
    keywords: 'application field support Dallas TX, app support Texas, enterprise software support DFW, on-site application support',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Application Field Support' },
  },
  '/services/app/infrastructure': {
    title: 'Database & App Infrastructure Support Services | Cendien',
    description:
      'Cendien provides database administration, server management and application infrastructure support for enterprises across Texas and the U.S.',
    keywords: 'database support Dallas TX, app infrastructure Texas, database administration DFW, SQL server support Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Database & Infrastructure Support' },
  },
  '/services/app/virtual-cio': {
    title: 'vCIO & Virtual CIO Consulting Services in Dallas | Cendien',
    description:
      'Cendien provides Virtual CIO and fractional IT leadership for growing businesses — strategic IT planning, vendor management and technology governance.',
    keywords: 'virtual CIO Dallas TX, fractional CIO Texas, vCIO services DFW, IT leadership consulting Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Virtual CIO' },
  },
  '/services/app/development-services': {
    title: 'Software Development & Full-Stack Dev Services | Cendien',
    description:
      'Cendien delivers modern full-stack software development, API integration and DevOps services for enterprise clients across Texas and nationwide.',
    keywords: 'software development Dallas TX, full stack development Texas, DevOps services DFW, API development Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Software Development Services' },
  },
  '/services/app/cloud-services': {
    title: 'Cloud-Native Application Development Services | Cendien',
    description:
      'Cendien designs and deploys cloud-native applications on AWS, Azure and GCP for Texas enterprises, including microservices, containers and serverless.',
    keywords: 'application cloud services Dallas TX, cloud-native development Texas, microservices DFW, serverless Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Application Cloud Services' },
  },
  '/services/strategy-advisory': {
    title: 'IT Strategy Advisory & Roadmap Planning Services | Cendien',
    description:
      'Cendien IT advisors build strategic technology roadmaps and IT governance frameworks for Texas enterprises, aligning technology with business goals.',
    keywords: 'IT strategy advisory Dallas TX, IT roadmap planning Texas, technology advisory DFW, IT governance Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'IT Strategy Advisory' },
  },
  '/services/digital-transformation': {
    title: 'Digital Transformation & Modernization Services | Cendien',
    description:
      'Cendien guides Texas enterprises through digital transformation — legacy modernization, cloud adoption, process automation and data-driven operations.',
    keywords: 'digital transformation Dallas TX, IT modernization Texas, legacy system modernization DFW, digital strategy Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Digital Transformation' },
  },
  '/services/tech-assessment': {
    title: 'Technology Assessment & IT Audit in Dallas | Cendien',
    description:
      "Cendien's technology assessments deliver a comprehensive IT audit, gap analysis and an actionable modernization roadmap for Texas businesses.",
    keywords: 'technology assessment Dallas TX, IT audit Texas, IT gap analysis DFW, technology audit Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Technology Assessment' },
  },
  '/services/managed-compliance': {
    title: 'Managed Compliance: HIPAA, SOC 2 & CMMC Programs | Cendien',
    description:
      'Cendien manages end-to-end IT compliance programs — HIPAA, SOC 2, CMMC and NIST — for healthcare, government and enterprise clients across Texas.',
    keywords: 'managed compliance Dallas TX, HIPAA compliance Texas, SOC 2 compliance DFW, CMMC compliance Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Managed Compliance' },
  },
  '/services/change-management': {
    title: 'IT Change Management & User Adoption Services | Cendien',
    description:
      'Cendien drives successful IT change with structured change management — stakeholder engagement, training, communication and adoption programs.',
    keywords: 'IT change management Dallas TX, organizational change management Texas, technology adoption DFW, change management Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'IT Change Management' },
  },
  '/industries': {
    title: 'Industries We Serve: Healthcare to Manufacturing | Cendien',
    description:
      'Cendien serves 12+ industries across Texas — healthcare, manufacturing, financial services, government, education, energy, retail and much more.',
    keywords: 'IT consulting industries Texas, healthcare IT, manufacturing IT, financial services IT, government IT DFW',
    schemaType: 'CollectionPage',
  },
  '/industries/healthcare': {
    title: 'Healthcare IT Consulting Services in Dallas | Cendien',
    description:
      'Cendien delivers healthcare IT consulting — EHR, interoperability, HIPAA and health data solutions — for hospitals and health systems across Texas.',
    keywords: 'healthcare IT consulting Dallas TX, hospital IT Texas, health system IT DFW, clinical IT Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Healthcare IT Consulting' },
  },
  '/industries/government': {
    title: 'Government IT Services Texas | DIR & DBITS Vendor | Cendien',
    description:
      'Cendien delivers municipal IT support Texas cities rely on and IT services for public sector Texas clients — plus IT staffing for public agencies.',
    keywords: 'municipal IT support Texas, IT services for public sector Texas, government IT contractor Texas DIR, IT staffing for public agencies, NCTCOG DBITS TXShare, Texas DIR vendor',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Government IT Services' },
  },
  '/industries/commercial': {
    title: 'Commercial Business IT Services in Dallas | Cendien',
    description:
      'Cendien delivers IT consulting, ERP, cloud and staffing for commercial enterprises across Dallas–Fort Worth and Texas, tailored to your operations.',
    keywords: 'commercial IT services Dallas TX, enterprise IT Texas, business IT consulting DFW, commercial technology',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Commercial Business IT' },
  },
  '/industries/transportation': {
    title: 'Transportation & Logistics IT Services in Texas | Cendien',
    description:
      'Cendien supports transportation and logistics companies with our IT consulting, fleet and TMS platforms, ERP and cloud solutions across Texas.',
    keywords: 'transportation IT Dallas TX, logistics IT Texas, fleet management IT DFW, supply chain IT Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Transportation IT Services' },
  },
  '/industries/education': {
    title: 'Education IT Services for K-12 & Higher Ed | Cendien',
    description:
      'Cendien provides IT consulting, ERP and managed services for K-12 schools, colleges and universities across Texas, with tight budget awareness.',
    keywords: 'education IT Dallas TX, K-12 IT Texas, university IT DFW, higher education IT consulting Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Education IT Services' },
  },
  '/industries/energy': {
    title: 'Energy & Utilities IT Consulting in Texas | Cendien',
    description:
      'Cendien delivers IT solutions for energy companies, utilities and oil & gas organizations across Texas — ERP, OT/IT integration and cybersecurity.',
    keywords: 'energy IT Dallas TX, oil gas IT Texas, utilities IT DFW, energy sector technology Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Energy IT Services' },
  },
  '/industries/manufacturing': {
    title: 'Manufacturing IT, ERP & MES Services in Texas | Cendien',
    description:
      'Cendien supports Texas manufacturers with ERP implementation, MES integration, supply chain IT and Industry 4.0 digital transformation services.',
    keywords: 'manufacturing IT Dallas TX, ERP for manufacturers Texas, MES integration DFW, manufacturing technology Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Manufacturing IT Services' },
  },
  '/industries/financial-services': {
    title: 'Financial Services IT & FinTech in Dallas | Cendien',
    description:
      'Cendien delivers IT consulting, compliance and managed services for banks, credit unions, insurance firms and fintech companies across Texas.',
    keywords: 'financial services IT Dallas TX, banking IT Texas, fintech consulting DFW, financial IT compliance Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Financial Services IT' },
  },
  '/industries/retail': {
    title: 'Retail IT: Omnichannel, POS & E-commerce | Cendien',
    description:
      'Cendien delivers modern ERP, e-commerce integration, POS systems and managed IT for retail and consumer-goods companies across Texas and beyond.',
    keywords: 'retail IT Dallas TX, retail technology Texas, POS IT DFW, e-commerce IT Carrollton, omnichannel retail',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Retail IT Services' },
  },
  '/industries/nonprofit': {
    title: 'Nonprofit IT Services & Managed IT Support | Cendien',
    description:
      'Cendien provides cost-effective IT consulting, managed services and cloud solutions tailored for nonprofits and NGOs across Texas and the U.S.',
    keywords: 'nonprofit IT Dallas TX, NGO IT services Texas, nonprofit technology DFW, charity IT consulting Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Nonprofit IT Services' },
  },
  '/industries/legal': {
    title: 'Legal Industry IT Services for Law Firms | Cendien',
    description:
      'Cendien delivers IT consulting, document management, cybersecurity and compliance solutions for law firms and legal organizations across Texas.',
    keywords: 'legal IT services Dallas TX, law firm IT Texas, legal technology DFW, document management law firms Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Legal IT Services' },
  },
  '/industries/hospitality': {
    title: 'Hospitality IT Services for Hotels & Restaurants | Cendien',
    description:
      'Cendien supports hotels, resorts and restaurants with PMS integration, Wi-Fi, POS and network infrastructure and managed IT across Texas and nationwide.',
    keywords: 'hospitality IT Dallas TX, hotel IT services Texas, restaurant technology DFW, PMS IT Carrollton',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Hospitality IT Services' },
  },
  '/careers': {
    title: 'IT Consulting & Staffing Careers in Dallas | Cendien',
    description:
      'Join Cendien in Carrollton, TX. We hire ERP consultants, cloud engineers, recruiters, analysts and technology professionals. View our open positions.',
    keywords: 'Cendien careers, IT jobs Dallas TX, ERP consultant jobs Texas, cloud engineer jobs DFW, IT staffing jobs Carrollton',
    schemaType: 'WebPage',
  },
  '/insights': {
    title: 'IT Insights & Thought Leadership Blog Posts | Cendien',
    description:
      'Explore Cendien Insights — expert articles on ERP, cloud, AI, cybersecurity, digital transformation and IT staffing from our Texas consulting team.',
    keywords: 'IT insights blog, ERP articles, cloud AI technology blog, IT consulting blog Texas, digital transformation insights',
    schemaType: 'Blog',
  },
  '/newsroom': {
    title: 'Cendien Newsroom: Press Releases & Company News | Cendien',
    description:
      'Press releases and company news from Cendien, an IT consulting and managed services firm in Carrollton, Texas. Read our latest announcements and media kit.',
    keywords: 'Cendien newsroom, Cendien press releases, Cendien company news, IT services news Texas, Cendien media kit',
    schemaType: 'CollectionPage',
  },
  '/search': {
    title: 'Search Our Site: IT Services & Insights Hub | Cendien',
    description:
      'Search the Cendien site for IT services, industries, insights, resources, case studies and careers. Find exactly what you need across our entire website.',
    keywords: 'search Cendien, find IT services, ERP consulting search',
    schemaType: 'WebPage',
  },
  '/originals': {
    title: 'Cendien Originals: Comedy Series About IT, ERP & AI | Cendien',
    description:
      'Watch Cendien Originals, short comedy series about the IT problems every organization knows: the IT guy who retires Friday, the approvals stuck with Brenda and more. New episodes on a schedule.',
    keywords: 'Cendien Originals, IT comedy series, managed IT video, ERP comedy, IT documentation, Cendien episodes',
    schemaType: 'CollectionPage',
  },
  '/videos': {
    title: 'How-To Videos & Service Explainer Library | Cendien',
    description:
      'Watch Cendien videos — step-by-step how-to guides and short service explainers on ERP, managed IT, cloud and IT staffing, with full transcripts.',
    keywords: 'Cendien videos, IT how-to videos, ERP how-to, managed IT explainer, cloud tutorial Dallas',
    schemaType: 'CollectionPage',
  },
  '/videos/how-tos': {
    title: 'IT How-To Videos: Step-by-Step IT Tutorials | Cendien',
    description:
      'Every step-by-step guide in the Cendien how-to video series — payroll, ERP, cloud and IT operations walkthroughs with written steps and transcripts.',
    keywords: 'IT how-to videos, payroll tax update checklist, ERP how-to guides, step-by-step IT tutorials',
    schemaType: 'CollectionPage',
  },
  '/videos/explainers': {
    title: 'Service Explainer Videos: ERP, IT & Staffing | Cendien',
    description:
      'Short service explainer videos from Cendien — about a minute each on managed IT, Infor and Workday support and IT staffing, with full transcripts.',
    keywords: 'Cendien explainer videos, managed IT explainer, Infor ERP video, IT staffing video Dallas',
    schemaType: 'CollectionPage',
  },
  '/videos/training': {
    title: 'Training Videos: Infor, Workday, Oracle & ERP | Cendien',
    description:
      'Longer-format Cendien training videos for employees and clients — guided walkthroughs of the platforms we support, plus managed IT and AI tools.',
    keywords: 'Cendien training videos, Infor CloudSuite training, Workday training videos, ERP walkthroughs, managed IT training, AI tools training',
    schemaType: 'CollectionPage',
  },
  '/academy': {
    title: 'Cendien Academy: Courses & Learning Paths | Cendien',
    description:
      'Explore the Cendien Academy — structured courses, guided walkthroughs and learning paths across ERP, AI, cloud, security and managed IT.',
    keywords: 'Cendien Training Academy, ERP training courses, AI agent training, cloud and security training, learning paths',
    schemaType: 'CollectionPage',
  },
  '/academy/catalog': {
    title: 'Training Catalog: Courses by Platform & Role | Cendien',
    description:
      'Search and filter the Cendien training catalog by platform, product, role, level and format — published, reviewable courses across ERP, AI, cloud and IT.',
    keywords: 'training catalog, ERP course catalog, filter IT courses by role, platform training, Cendien courses',
    schemaType: 'CollectionPage',
  },
  '/academy/paths': {
    title: 'Learning Paths for ERP, Cloud & AI Roles | Cendien',
    description:
      'Ordered learning paths that sequence Cendien courses toward a role outcome — consultant, administrator, developer, cloud operations and security awareness.',
    keywords: 'learning paths, IT role training paths, ERP consultant path, cloud operations path, security awareness path',
    schemaType: 'CollectionPage',
  },
  '/academy/resources': {
    title: 'Training Resources & Downloadable Course Guides | Cendien',
    description:
      'Exercise files, checklists and reference material that accompany reviewed Cendien training courses, published per course once cleared for release.',
    keywords: 'training resources, course exercise files, IT training downloads, Cendien course material',
    schemaType: 'CollectionPage',
  },
  '/academy/team-training': {
    title: 'Request Team Training & Corporate Cohorts | Cendien',
    description:
      'Request team or corporate training from Cendien. Tell us your platform, learners and environment and we scope cohorts, format and curriculum around you.',
    keywords: 'request team training, corporate IT training, ERP team training, custom training cohorts, Cendien training request',
    schemaType: 'WebPage',
  },
  '/academy/my-learning': {
    title: 'My Learning Dashboard: Courses & Progress | Cendien',
    description:
      'The Cendien Training Academy learner dashboard — track your enrollment, in-progress courses, assessments and certificates for signed-in learners.',
    keywords: 'my learning, learner dashboard, Cendien training progress, course enrollment',
    schemaType: 'WebPage',
  },
  '/academy/access': {
    title: 'Redeem Your Training Access Code | Cendien Academy',
    description:
      'Enter the access code Cendien issued you along with your email to unlock member training courses instantly, with no login or SSO required at all.',
    keywords: 'redeem training access code, Cendien academy access, unlock training, code based access, no login training',
    schemaType: 'WebPage',
  },
  '/academy/learn/infor-hcm-ai-agents/building-tools': {
    title: 'Building Tools — Infor HCM AI Agent Training | Cendien',
    description:
      "Lesson 1 of Cendien's Infor HCM AI Agent training track: a guided video walkthrough on building the tools an AI agent uses in an Infor HCM environment.",
    keywords: 'Infor HCM AI agent training, building tools, Infor agent lesson, Infor HCM video training',
    schemaType: 'WebPage',
  },
  '/academy/learn/infor-hcm-ai-agents/building-agents': {
    title: 'Building Agents — Infor HCM AI Agent Training | Cendien',
    description:
      "Lesson 2 of Cendien's Infor HCM AI Agent training track: a guided video walkthrough on building an AI agent within an Infor HCM environment.",
    keywords: 'Infor HCM AI agent training, building agents, Infor agent lesson, Infor HCM video training',
    schemaType: 'WebPage',
  },
  '/academy/learn/workday-hcm-ai-agents/build-register-hcm-agent': {
    title: 'Build & Register an HCM Agent — Workday Training | Cendien',
    description:
      "Lesson 1 of Cendien's Workday HCM AI Agent training track: a guided video walkthrough on building and registering an HCM agent in Workday HCM.",
    keywords: 'Workday HCM AI agent training, build and register an HCM agent, Workday agent lesson, Workday video training',
    schemaType: 'WebPage',
  },
  '/academy/learn/workday-hcm-ai-agents/configure-authorize-test': {
    title: 'Configure, Authorize & Test — Workday Training | Cendien',
    description:
      "Lesson 2 of Cendien's Workday HCM AI Agent training track: a guided video walkthrough on configuring, authorizing and testing an HCM agent in Workday.",
    keywords: 'Workday HCM AI agent training, configure authorize and test, Workday agent lesson, Workday video training',
    schemaType: 'WebPage',
  },
  '/blog': {
    title: 'IT Strategy, ERP & Cloud Blog Articles in Dallas | Cendien',
    description:
      "Read the latest IT strategy, ERP, cloud, AI and cybersecurity articles from Cendien's team of Texas IT consultants, engineers and technology leaders.",
    keywords: 'IT blog Dallas TX, ERP blog Texas, cloud blog DFW, AI blog IT consulting, technology articles Carrollton',
    schemaType: 'Blog',
  },
  '/resources': {
    title: 'IT Resources: Whitepapers, Guides & Checklists | Cendien',
    description:
      'Download Cendien IT resources — whitepapers, implementation guides, checklists and case studies covering ERP, cloud, AI and managed IT topics.',
    keywords: 'IT resources, ERP whitepapers, cloud implementation guide, IT checklists, managed services guide Texas',
    schemaType: 'CollectionPage',
  },
  '/case-studies': {
    title: 'Client Case Studies: ERP, Cloud & Managed IT | Cendien',
    description:
      'Explore the Cendien case studies — proven client outcomes across Workday support, ERP, healthcare IT, cloud migration and managed IT services.',
    keywords: 'IT case studies, Workday managed services, ERP case study, cloud migration case study, healthcare IT results',
    schemaType: 'CollectionPage',
  },
  '/government-contracting': {
    title: 'Government Contracting: DBITS & TXShare Programs | Cendien',
    description:
      'Cendien is procurement-ready for public agencies via NCTCOG DBITS (TXShare), Texas DIR Vendor registration, Certified MBE (NCTRCA) and NAICS 541512/541519.',
    keywords: 'government IT contractor Texas, NCTCOG DBITS, TXShare cooperative purchasing, Texas DIR vendor, Certified MBE NCTRCA, NAICS 541512, public sector IT staffing Dallas',
    schemaType: 'ProfessionalService',
    schemaExtras: { serviceType: 'Government Contracting' },
  },
  '/engagement-models': {
    title: 'IT Engagement Models: Staffing & Managed Teams | Cendien',
    description:
      'Explore how to work with Cendien — IT staff augmentation, managed teams, managed services, fixed-fee projects, service desk and direct hire across Texas.',
    keywords: 'IT engagement models, IT staff augmentation Texas, managed IT teams Dallas, fixed fee IT projects, IT service desk DFW',
    schemaType: 'WebPage',
  },
  '/request-consultants': {
    title: 'Request IT Consultants: ERP, Cloud & AI Experts | Cendien',
    description:
      'Request experienced IT consultants from Cendien for ERP, cloud, AI, cybersecurity and staffing needs. Submit requirements and we respond within 24 hours.',
    keywords: 'request IT consultants, hire ERP consultant Texas, find cloud consultant Dallas, IT consulting request DFW',
    schemaType: 'WebPage',
  },
  '/partnerships': {
    title: 'Technology Partners: Infor, Microsoft, Oracle | Cendien',
    description:
      'Cendien is a partner of Infor, Microsoft and Oracle, bringing best-in-class ERP, cloud and database platforms to clients across Texas and the U.S.',
    keywords: 'Cendien partnerships, Microsoft partner Texas, Infor partner, Oracle partner DFW',
    schemaType: 'WebPage',
  },
  '/partnerships/infor': {
    title: 'Infor ERP Partner & Consulting Services in Texas | Cendien',
    description:
      'Cendien is a certified Infor ERP partner delivering implementation, upgrades, integration and support for Infor CloudSuite and Lawson across Texas.',
    keywords: 'Infor ERP partner Dallas TX, Infor consulting Texas, Infor implementation DFW, Infor certified partner',
    schemaType: 'WebPage',
  },
  '/partnerships/microsoft': {
    title: 'Microsoft Partner: Dynamics 365, Azure & M365 | Cendien',
    description:
      'Cendien is a certified Microsoft partner specializing in Dynamics 365 ERP and CRM, Azure cloud and Microsoft 365 implementations for Texas enterprises.',
    keywords: 'Microsoft partner Dallas TX, Dynamics 365 partner Texas, Azure partner DFW, Microsoft 365 consulting Carrollton',
    schemaType: 'WebPage',
  },
  '/partnerships/aws': {
    title: 'AWS Cloud Migration & Managed AWS Services | Cendien',
    description:
      'Cendien delivers AWS cloud migrations, managed AWS infrastructure and AI/ML solutions on Amazon Web Services for clients across Texas and nationwide.',
    keywords: 'AWS partner Dallas TX, Amazon Web Services partner Texas, AWS migration DFW, AWS managed services Carrollton',
    schemaType: 'WebPage',
  },
  '/partnerships/connectwise': {
    title: 'ConnectWise Solutions, ITSM & PSA Support | Cendien',
    description:
      'Cendien supports ConnectWise — helping IT service providers implement, integrate and optimize ConnectWise PSA and RMM across Dallas–Fort Worth.',
    keywords: 'ConnectWise solutions Dallas TX, ConnectWise support, ITSM Texas, PSA software DFW, ConnectWise Manage Automate',
    schemaType: 'WebPage',
  },
  '/partnerships/oracle': {
    title: 'Oracle Partner: Cloud ERP, EBS & Database | Cendien',
    description:
      'Cendien is an Oracle partner delivering Cloud ERP, E-Business Suite, NetSuite, database engineering and integration services across Texas and nationwide.',
    keywords: 'Oracle partner Dallas TX, Oracle Cloud ERP consulting Texas, E-Business Suite partner DFW, Oracle database support',
    schemaType: 'WebPage',
  },
  '/service-areas': {
    title: 'IT Service Areas in Dallas–Fort Worth, TX | Cendien',
    description:
      'Cendien serves Dallas–Fort Worth from Carrollton, TX — managed IT, ERP, security, staffing and AI across Dallas, Tarrant, Collin and Denton counties.',
    keywords: 'IT service areas Dallas Fort Worth, managed IT DFW, ERP consulting Dallas, IT staffing Fort Worth, cybersecurity Dallas TX',
    schemaType: 'CollectionPage',
  },
  '/privacy-policy': {
    title: 'Privacy Policy, TDPSA Compliance & Your Data | Cendien',
    description:
      'Read the Cendien privacy policy. We are TDPSA compliant and committed to protecting your personal data across our site and services. Last updated 2026.',
    keywords: 'Cendien privacy policy, TDPSA compliance, data privacy Texas IT firm',
    schemaType: 'WebPage',
  },
  '/terms-of-service': {
    title: 'Terms of Service: Website Use & Legal Terms | Cendien',
    description:
      'Review the Cendien Terms of Service governing use of our website and IT consulting, staffing and managed services, governed by the laws of Texas.',
    keywords: 'Cendien terms of service, IT consulting terms Texas, service agreement Carrollton',
    schemaType: 'WebPage',
  },
};

/** Fallback SEO for unknown routes */
export const DEFAULT_SEO: PageSEOMeta = {
  title: 'IT Consulting, Staffing & Managed IT Services | Cendien',
  description:
    'Cendien is an IT consulting, staffing and managed services firm in Carrollton, TX, offering ERP, cloud, AI and digital transformation services.',
  keywords: 'IT consulting, IT staffing, ERP, cloud, AI, Carrollton TX',
  schemaType: 'WebPage',
};